import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { label: 'Floatels', path: '/services/floatels' },
    { label: 'Camps', path: '/services/camps' },
    { label: 'Hotel management', path: '/services/hotel-management' },
    { label: 'Site office', path: '/services/site-office' },
    {
      label: 'Facility Services',
      path: '#',
      subLinks: [
        { label: 'Catering', path: '/services/catering' },
        { label: 'Housekeeping', path: '/services/housekeeping' },
        { label: 'Laundry', path: '/services/laundry' },
      ],
    },
    { label: 'Personnel Transfer Baskets', path: '/services/personnel-transfer-baskets' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 bg-white shadow-sm border-b border-slate-100 ${
        isScrolled 
          ? 'py-4 md:py-5' 
          : 'py-6 md:py-8'
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/sitefacility-0-1.png" 
            alt="Site Facility Logo" 
            className="h-10 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-102"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-3 min-[1350px]:gap-5">
          {navLinks.map((link) => {
            const hasSubLinks = Boolean(link.subLinks && link.subLinks.length > 0);
            const isParentActive = hasSubLinks
              ? link.subLinks.some((sub) => isActive(sub.path))
              : isActive(link.path);

            return (
              <div key={link.label} className="relative group">
                {link.path && link.path !== '#' ? (
                  <Link
                    to={link.path}
                    className={`text-xs min-[1350px]:text-sm font-medium tracking-tight transition-colors duration-200 py-2 flex items-center gap-1.5 whitespace-nowrap ${
                      isParentActive
                        ? 'text-accent font-semibold'
                        : 'text-primary/80 hover:text-accent'
                    }`}
                  >
                    {link.label}
                    {hasSubLinks && (
                      <i className="fa-regular fa-chevron-down text-[10px] transition-transform duration-200 group-hover:rotate-180" />
                    )}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={`text-xs min-[1350px]:text-sm font-medium tracking-tight transition-colors duration-200 py-2 flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      isParentActive
                        ? 'text-accent font-semibold'
                        : 'text-primary/80 hover:text-accent'
                    }`}
                  >
                    {link.label}
                    {hasSubLinks && (
                      <i className="fa-regular fa-chevron-down text-[10px] transition-transform duration-200 group-hover:rotate-180" />
                    )}
                  </button>
                )}

                {/* Dropdown Menu */}
                {hasSubLinks && (
                  <div className="absolute left-0 top-full mt-1 w-60 rounded-xl bg-white border border-slate-100 shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-1 group-hover:translate-y-0 z-50">
                    {link.subLinks.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className={`block px-5 py-2.5 text-xs font-medium tracking-wide transition-colors duration-200 ${
                          isActive(sub.path)
                            ? 'bg-muted-bg text-accent font-semibold'
                            : 'text-primary/70 hover:bg-slate-50 hover:text-accent'
                        }`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          
          {/* CTA Button */}
          <Link
            to="/contact"
            className="ml-4 py-2.5 px-5 rounded-xl bg-primary text-white text-xs font-semibold hover:bg-primary-light transition-all-300 shadow-sm"
          >
            Get in touch
          </Link>
        </nav>

        {/* Mobile Menu Button (< 1200px) */}
        <button
          className="flex min-[1200px]:hidden h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-primary hover:bg-slate-50 transition-colors duration-200"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          <i className={`fa-regular ${isOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} />
        </button>
      </div>

      {/* Mobile Navigation Panel (< 1200px) */}
      <div 
        className={`fixed inset-x-0 top-[73px] md:top-[97px] bottom-0 z-30 bg-white border-t border-slate-100 transition-all duration-300 min-[1200px]:hidden ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div className="h-full overflow-y-auto px-6 py-8 flex flex-col justify-between">
          <nav className="space-y-6">
            {navLinks.map((link) => {
              const hasSubLinks = Boolean(link.subLinks && link.subLinks.length > 0);
              const isParentActive = hasSubLinks
                ? link.subLinks.some((sub) => isActive(sub.path))
                : isActive(link.path);

              return (
                <div key={link.label} className="space-y-3">
                  {link.path && link.path !== '#' ? (
                    <Link
                      to={link.path}
                      className={`block text-lg font-semibold tracking-wide ${
                        isParentActive ? 'text-accent' : 'text-primary'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <span className={`block text-lg font-semibold tracking-wide ${
                      isParentActive ? 'text-accent' : 'text-primary'
                    }`}>
                      {link.label}
                    </span>
                  )}

                  {hasSubLinks && (
                    <div className="pl-4 border-l-2 border-accent/20 space-y-3 my-2">
                      {link.subLinks.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          className={`block text-sm font-medium tracking-wide ${
                            isActive(sub.path) ? 'text-accent font-semibold' : 'text-primary/70 hover:text-accent'
                          }`}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="pt-8 border-t border-slate-100 space-y-4">
            <Link
              to="/contact"
              className="block w-full py-4 px-6 rounded-xl bg-primary text-white text-center font-semibold hover:bg-primary-light transition-all duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
